import { useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { db, functions } from '@/lib/firebase';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';

/**
 * The numbers on the panel sidebars (functions/src/notifications/panel-badges):
 * assessments waiting for a mark, and what's new in Enrollments, Certificates
 * and Earnings since the person last opened them.
 */
export type PanelName = 'instructor' | 'seller';
export type BadgeSection = 'assessments' | 'enrollments' | 'certificates' | 'earnings';
export type PanelBadges = Record<BadgeSection, number>;

const key = (uid: string | undefined, panel: PanelName) => ['panel-badges', uid, panel];

export function usePanelBadges(panel: PanelName) {
  const { user } = useFirebaseAuth();
  return useQuery({
    queryKey: key(user?.id, panel),
    queryFn: async () =>
      (await httpsCallable<{ panel: PanelName }, { badges: PanelBadges }>(functions, 'getPanelBadges')({ panel })).data.badges,
    enabled: !!user?.id,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** A page calls this when it opens: its "new since last time" count starts again. */
export function useMarkPanelSeen(panel: PanelName, section: Exclude<BadgeSection, 'assessments'>) {
  const { user } = useFirebaseAuth();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!user?.id) return;
    // Its own record: written into the settings record (preferences/default)
    // it raced the settings read, and the page showed defaults -- an empty
    // certificate name -- though the real values were saved.
    const ref = doc(db, 'users', user.id, 'preferences', 'panelSeen');
    setDoc(ref, { [panel]: { [section]: serverTimestamp() } }, { merge: true })
      .then(() => queryClient.invalidateQueries({ queryKey: key(user.id, panel) }))
      .catch((e) => console.warn('[panel-badges] could not mark seen:', e));
  }, [user?.id, panel, section, queryClient]);
}
